<script setup lang="ts">
import MessageVue from '@/components/assistant/chat/Message.vue'
import type {CreditUsage} from '@/client/mentor/CreditUsageClient'
import type {Message, UserMessageContent} from '@/client/mentor/MessagesClient'
import {FETCH_NEXT_PAGE_DELAY_MS, FETCH_NEXT_PAGE_SCROLL_TOP_THRESHOLD_PX} from '@/components/assistant/constants'
import {assistantChatMessageContextKey} from '@/components/assistant/chat/messageContext'
import {computed, nextTick, onMounted, provide, reactive, ref, toRef, watch} from 'vue'
import {promiseTimeout} from '@vueuse/core'
import {useAssistantChatPrintContext} from '@/components/assistant/composables/useAssistantChatPrintContext'
import {useAssistantViewport} from '@/components/assistant/composables/useAssistantViewport'

defineEmits<{
  selectPreviousMessage: [content: UserMessageContent]
  selectSuggestion: [suggestion: string]
}>()

const props = defineProps<{
  companyId: number
  creditUsage?: CreditUsage
  disabled?: boolean
  messages: Message[]
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  isLoading?: boolean
  fetchNextPage?: () => Promise<unknown>
}>()

const {getPrintSenderLabel, getPrintTimestamp} = useAssistantChatPrintContext()

const {isMobileVersion} = useAssistantViewport()
const toggleVisibilityByMessageKey = reactive(new Map<string, boolean>())

function getToggleStorageKey(messageId: number, toggleKey: string) {
  return `${messageId}:${toggleKey}`
}

provide(assistantChatMessageContextKey, {
  companyId: toRef(props, 'companyId'),
  creditUsage: toRef(props, 'creditUsage'),
  isMobileVersion,
  getPrintSenderLabel,
  getPrintTimestamp,
  getToggleVisible: (messageId, toggleKey) => {
    const storageKey = getToggleStorageKey(messageId, toggleKey)
    return toggleVisibilityByMessageKey.get(storageKey)
  },
  getOrInitToggleVisible: (messageId, toggleKey, defaultVisible) => {
    const storageKey = getToggleStorageKey(messageId, toggleKey)
    const existingValue = toggleVisibilityByMessageKey.get(storageKey)
    if (existingValue !== undefined) {
      return existingValue
    }

    toggleVisibilityByMessageKey.set(storageKey, defaultVisible)
    return defaultVisible
  },
  setToggleVisible: (messageId, toggleKey, visible) => {
    toggleVisibilityByMessageKey.set(getToggleStorageKey(messageId, toggleKey), visible)
  },
})

const messagesContainer = ref<HTMLElement | null>(null)
const isWaitingForNextPage = ref(false)

const lastMessage = computed(() => props.messages.at(-1))

onMounted(async () => {
  await scrollToBottom()
  await fetchAndFillScreen()
})

watch(
  () => props.messages.length,
  async (newLength, oldLength) => {
    if (newLength > (oldLength || 0)) {
      await nextTick()

      if (!oldLength) {
        await scrollToBottom()
      }

      await fetchAndFillScreen()
    }
  }
)

watch(
  () => lastMessage.value?.id,
  async (newLastMessageId, oldLastMessageId) => {
    if (newLastMessageId === undefined || newLastMessageId === oldLastMessageId) return
    await scrollToBottom()
  }
)

async function scrollToBottom() {
  await nextTick()
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
  }
}

async function fetchAndFillScreen() {
  const container = messagesContainer.value

  if (!canFetchMore() || !container) return
  const isScrollbarVisible = container.scrollHeight > container.clientHeight

  if (!isScrollbarVisible) {
    const previousScrollHeight = container.scrollHeight

    await props.fetchNextPage?.()
    await nextTick()

    container.scrollTop = container.scrollHeight - previousScrollHeight
  }
}

function canFetchMore() {
  return !!props.fetchNextPage && !!props.hasNextPage && !props.isFetchingNextPage && !isWaitingForNextPage.value
}

async function onScroll() {
  const container = messagesContainer.value
  if (!canFetchMore() || !container || container.scrollTop > FETCH_NEXT_PAGE_SCROLL_TOP_THRESHOLD_PX) return

  const previousScrollHeight = container.scrollHeight
  const previousScrollTop = container.scrollTop

  isWaitingForNextPage.value = true
  try {
    await promiseTimeout(FETCH_NEXT_PAGE_DELAY_MS)

    const currentContainer = messagesContainer.value
    if (
      !props.fetchNextPage ||
      !props.hasNextPage ||
      props.isFetchingNextPage ||
      !currentContainer ||
      currentContainer.scrollTop > FETCH_NEXT_PAGE_SCROLL_TOP_THRESHOLD_PX
    ) {
      return
    }

    await props.fetchNextPage?.()
    await nextTick()

    currentContainer.scrollTop = currentContainer.scrollHeight - previousScrollHeight + previousScrollTop
  } finally {
    isWaitingForNextPage.value = false
  }
}
</script>

<template>
  <div
    ref="messagesContainer"
    class="relative w-full h-full overflow-y-auto p-4 flex flex-col gap-4 assistant-print-content"
    @scroll="onScroll"
  >
    <div
      v-if="isLoading || isFetchingNextPage || isWaitingForNextPage"
      class="absolute left-0 right-0 top-3 z-10 flex items-center justify-center"
    >
      <div class="px-4 py-1 bg-white/90 rounded-lg shadow-md text-sm text-darkgrey flex items-center gap-2">
        <i class="fa-solid fa-circle-notch fa-spin"></i>
        <span>{{ $t('assistant.chat.message.loadingMessages') }}</span>
      </div>
    </div>

    <div class="flex-1"></div>

    <MessageVue
      v-for="(message, index) in messages"
      :key="message.id"
      :disabled="disabled"
      :is-last-message="message.id === lastMessage?.id"
      :message="message"
      :previous-message="messages.at(index - 1)"
      @select-previous-message="$emit('selectPreviousMessage', $event)"
      @select-suggestion="$emit('selectSuggestion', $event)"
    />
  </div>
</template>
